import { CommonModule } from '@angular/common';
import { Component, computed, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputOtpModule } from 'primeng/inputotp';
import { CrossSellingService } from 'src/app/api';
import { CrossSellingQuestionVm } from 'src/app/api/model/crossSellingQuestionVm';
import { AppResource } from 'src/app/app.resource';
import { BaseComponent } from 'src/app/shared/components/base/base.component';
import { QuestionComponent } from 'src/app/shared/components/question/question.component';
import { ToggleQuestionComponent } from 'src/app/shared/components/toggle-question/toggle-question.component';
import { QuestionVM } from 'src/app/shared/models/model/concretes/components/question.model';
import { ResponseVM } from 'src/app/shared/models/model/concretes/components/response.model';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { SubscriptionService } from 'src/app/shared/services/subscription.service';
import * as _ from "lodash";
import { CrossSellingAnswerDto } from 'src/app/api/model/crossSellingAnswerDto';
import { SaveCrossSellingQuestionsCommand } from 'src/app/api/model/saveCrossSellingQuestionsCommand';
import { InputSwitchModule } from 'primeng/inputswitch';
import { CANAL_VENTE_C } from 'src/app/shared/constants/context.constants';
import { isPartner } from 'src/app/shared/utils/brand-utils';
import { environment } from 'src/environments/environment';
import { GtmEventServiceService } from 'src/app/shared/services/gtm-event-service.service';

@Component({
  selector: 'app-cross-selling',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    QuestionComponent,
    ToggleQuestionComponent,
    InputOtpModule,
    ButtonModule,
    DialogModule,
    InputSwitchModule,
  ],
  templateUrl: './cross-selling.component.html'
})
export class CrossSellingComponent extends BaseComponent implements OnInit, OnDestroy {

  isValid = computed(() => this.navigationService?.isStepValid());
  isLoading = false;
  displayError = false;
  triedSubmit = false;
  customerExperience = false; // Defines if should display the page for customer or for prospect
  hideSubmitButton = false;
  ignoreSubmission = false;

  crossSellingQuestions = new Array<CrossSellingQuestionVm>();
  questions: Array<QuestionVM> | null = null;

  contrat_ref: string = this.store.contract().RefContrat ?? '-';

  get isPartner() {
    return isPartner(environment);
  }

  constructor(
    resources: AppResource,
    navigationService: NavigationService,
    private subscriptionService: SubscriptionService,
    private crossSellingService: CrossSellingService,
    private gtmEventService: GtmEventServiceService,
  ) {
    super(resources, navigationService);

    this.form = new FormGroup({
      marketing_phone_consent: new FormControl<boolean>(false),
      marketing_email_opposition: new FormControl<boolean>(false),
    });
    this.store.updateBreadcrumb(false);
  }

  override ngOnInit() {
    if (this.store.context()?.canalVente === CANAL_VENTE_C) {
      this.customerExperience = false;
    }
    else {
      this.customerExperience = true;
      if (this.store.contract()?.EstSigne === false) {
        this.ignoreSubmission = true;
        this.nextStep();
        return;
      }
      else {
        // Do nothing, the agreement SalesChannel will be retrieved with questions and if equals to "C" the questions will not be displayed

        // Log GTM event only for canalVente W and only when displayed after contract signature.
        this.logGtmEvent();
      }
    }

    this.form.statusChanges.subscribe(() => {
      this.navigationService?.updateStepValidity(true);
    });
    this.getQuestions();
  }

  ngOnDestroy(): void {
    this.store.updateBreadcrumb(true);
  }

  getQuestions() {
    let agreementId = this.customerExperience ? this.store.contract().NumeroContrat : this.store.devis().AgreementId?.toString();
    if (_.isNil(agreementId) || agreementId === '') {
      console.error("AgreementId is undefined or null");
      if (this.customerExperience) {
        this.nextStep();
      }
      return;
    }

    this.crossSellingService.apiCrosssellingsAgreementAgreementIdQuestionsGet(agreementId, this.store.context().canalVente ?? "").subscribe(information => {
      if (this.customerExperience && (information?.salesChannel === CANAL_VENTE_C ||
        _.isNil(information?.questions) ||
        information.questions.length === 0)) {
        console.log("Les questions ont déjà été répondues lors de la souscription, ou ne sont pas proposés aux partenaires.");
        this.ignoreSubmission = true;
        this.nextStep();
        return;
      }

      if (_.isNil(information?.questions) || information.questions.length === 0) {
        console.log("Pas de questions additionnelles à poser.");
      }

      this.crossSellingQuestions = information?.questions ?? [];

      this.crossSellingQuestions.forEach(q => {
        this.form.addControl(`question-${q.questionId}`, new FormControl('', Validators.required));
      });

      this.form.controls['marketing_phone_consent'].setValue(information?.isMarketingPhoneConsent ?? false);
      this.form.controls['marketing_email_opposition'].setValue(!(information?.isMarketingEmailConsent ?? true));

      this.questions = _.orderBy(this.crossSellingQuestions, ['order'], ['asc']).map(q => {
        return new QuestionVM(
          q.questionText!,
          undefined,
          [
            new ResponseVM(this.resource.form.no, false, undefined, !_.isNil(q.answer) && q.answer === false ? true : false),
            new ResponseVM(this.resource.form.yes, true, undefined, !_.isNil(q.answer) && q.answer === true ? true : false)
          ],
          q.questionId);
      });
    });
  }

  nextStep() {
    if (this.ignoreSubmission) {
      this.subscriptionService.nextStep();
      this.ignoreSubmission = false;
      return;
    }

    if (this.form.invalid) {
      this.triedSubmit = true;
      return;
    }

    let answers: CrossSellingAnswerDto[] = [];
    this.questions?.forEach(q => {
      let answer = this.form.get(`question-${q.id}`)?.value;
      if (!_.isNil(answer)) {
        answers.push({ questionId: q.id, answer: answer.toString() });
      }
    });

    let command: SaveCrossSellingQuestionsCommand = {
      agreementId: this.store.contract().NumeroContrat,
      answers: answers,
      marketingPhoneConsent: this.form.get('marketing_phone_consent')?.value,
      marketingEmailConsent: !this.form.get('marketing_email_opposition')?.value,
    };

    this.crossSellingService.apiCrosssellingAnswsersPatch(command).subscribe({
      next: () => {
        console.log('Questions additionnelles envoyées avec succès.');
        this.hideSubmitButton = true;
        if (this.customerExperience) {
          this.subscriptionService.nextStep();
        }
      },
      error: err => {
        console.error('Erreur lors de l\'envoi des questions additionnelles :', err);
        this.displayError = true;
      },
      complete: () => {
        this.isLoading = false;
        this.displayError = false;
      }
    });
  }

  async logGtmEvent(): Promise<void> {
    // here we use originalCanalVente instead of canalVente, as we need to know how the sale was initiated.
    let isWebChannel = this.store.context()?.originalCanalVente !== CANAL_VENTE_C;
    if (isWebChannel === false) return;

    await this.gtmEventService.pushEvent('contract_signature_submit');
  }
}
